import React, { JSX, useState } from 'react';
import {
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/system/Stack';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import { FilterListOff } from '@mui/icons-material';
import Iconify from '../../iconify';
import Label from '../../label';
import CustomPopover, { usePopover } from '../../custom-popover';
import Scrollbar from '../../scrollbar';

interface MultiSelectProps {
  indexKey: string;
  options: { label: string; id: string }[];
  value: any[];
  handleFilter: (key: string, newValue: any[]) => void;
  icon?: any;
  size?: number;
  height?: number;
  useLabel?: boolean;
  margin?: number;
  renderItem?: (item: any) => JSX.Element;
}

const MultiObjectSelect: React.FC<MultiSelectProps> = ({
  indexKey,
  options,
  value,
  handleFilter,
  icon,
  size = 0,
  height = 53,
  useLabel = true,
  margin = 2,
  renderItem,
}) => {
  const { t } = useTranslation();
  const popover = usePopover();
  const theme = useTheme();

  const [searchText, setSearchText] = useState<string>('');
  const maxHeight = 390;

  return (
    <Stack m={margin} sx={{ minWidth: size > 0 ? size : 'auto', width: 'auto' }}>
      {useLabel && (
        <InputLabel
          shrink
          sx={{
            ml: 1,
            pr: 2,
            color: `${theme.palette.text.primary} !important`,
            fontSize: '1.1rem !important',
          }}
        >
          {t(`text.labels.${indexKey}`)}
        </InputLabel>
      )}
      <Button
        variant="contained"
        onClick={popover.onOpen}
        disabled={options.length === 0}
        startIcon={icon}
        endIcon={
          <Iconify
            icon={popover.open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
            sx={{ ml: -0.5, color: theme.palette.action.active }}
          />
        }
        sx={{
          py: 0,
          px: 2,
          backgroundColor: 'background.paper',
          color: 'text.disabled',
          height,
          justifyContent: 'space-between',
          border: `solid 1px ${theme.palette.divider}`,
          '&:hover': {
            border: `solid 1px ${theme.palette.action.active}`,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Typography
          variant="inherit"
          fontSize={14}
          fontWeight={500}
          sx={{
            textTransform: 'capitalize',
            textAlign: 'left',
            width: '100%',
            color: 'text.disabled',
          }}
        >
          {t(`text.labels.${indexKey}`)}
          {value?.length > 0 && (
            <Label color="primary" sx={{ ml: 1 }}>
              {value.length}
            </Label>
          )}{' '}
        </Typography>
      </Button>
      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ p: 2.5, maxWidth: 420, maxHeight: 500, width: '100%' }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <TextField
              label={`${t(`text.labels.${indexKey}`)} ${t('text.labels.search').toLowerCase()}`}
              variant="filled"
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Tooltip title={t('text.buttons.reset')}>
              <IconButton onClick={() => handleFilter(indexKey, [])} disabled={value?.length === 0}>
                <Badge color="primary" variant="dot" invisible={value?.length === 0}>
                  <FilterListOff />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>

          <Scrollbar sx={{ maxHeight }}>
            <List>
              <Stack
                spacing={0.5}
                direction="column"
                width={1}
                px={0.5}
                divider={<Divider variant="middle" />}
              >
                {options
                  ?.filter((option) => option?.label.toLowerCase()?.includes(searchText))
                  .map((option, index) =>
                    renderItem ? (
                      renderItem(option)
                    ) : (
                      <ListItem key={index}>
                        <ListItemButton
                          onClick={(event) => {
                            event.stopPropagation();
                            const newValue =
                              value?.filter((v) => v.id === option.id).length > 0
                                ? value?.filter((v) => v.id !== option.id)
                                : [...value, option];
                            handleFilter(indexKey, newValue);
                          }}
                          sx={{
                            px: 0,
                          }}
                        >
                          <Checkbox
                            checked={value?.filter((v) => v.id === option.id).length > 0}
                            sx={{ color: theme.palette.action.active }}
                          />
                          <ListItemText
                            id={option.id}
                            primary={option.label}
                            sx={{
                              textTransform: 'capitalize',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )
                  )}
              </Stack>
            </List>
          </Scrollbar>
        </Stack>
      </CustomPopover>
    </Stack>
  );
};

export default MultiObjectSelect;
